import { useEffect, useRef, useState } from "react";
import Spinner from "../components/Spinner";
import RegenerateBar from "../components/RegenerateBar";
import { callAI, normalizeAddressKey, track } from "../lib/api";
import { renderMD } from "../lib/markdown";
import { getStageState } from "../lib/journey";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../components/AuthGate";
import { openDisclosurePDF } from "../../lib/disclosurePDF";

// Stage 3 — Disclosures.
//
// AI-led interview that maps to whatever seller disclosure obligations apply
// in the property's actual state (California's TDS/SPQ/NHD if it's a CA
// property, that state's equivalent form otherwise — lead-paint is a federal
// requirement for pre-1978 homes regardless of state).
// Reads inspection findings from the Prep stage if available — those become
// pre-filled "yes" answers (inspector saw it = legally known defect).
//
// Two phases:
//   1. AI generates the tailored question set (skips items already known from
//      inspection findings).
//   2. Seller answers; AI synthesizes the disclosure summary that will be
//      rendered into formal forms.

const Q_SYSTEM = "You are a real estate disclosure specialist licensed across all US states. Determine the property's state from its address and produce a tight list of plain-English yes/no questions mapping to THAT state's standard seller property disclosure obligations (e.g. California's TDS/SPQ/NHD, or the equivalent seller disclosure statement used in the property's actual state), plus the federal lead-based-paint disclosure for pre-1978 homes and HOA disclosure if applicable. Tailor to the property's age and location. SKIP questions already answered by the pre-listing inspection findings — those are already known defects.";

const Q_PROMPT = (address, year, inspectionFindings) => `Address: ${address}
Year built: ${year ?? "unknown"}

Pre-listing inspection findings (already known defects — DO NOT ask about these):
${inspectionFindings && Object.keys(inspectionFindings).length ? JSON.stringify(inspectionFindings) : "(no inspection report yet — generate the full standard question set)"}

Output a JSON array of 6–10 questions. Focus on items an inspector CAN'T see (neighbor disputes, prior insurance claims, prior litigation, mold/leak history before inspection date, known HOA issues, undocumented additions, easements you're aware of, etc.). Each item:
{ "id": "kebab-case-id", "q": "the question", "section": "short category label appropriate to this property's actual state disclosure form (e.g. its state's transfer disclosure statement, property questionnaire, natural hazard disclosure, lead paint, HOA)", "note_required_if_yes": true|false }

JSON only. No commentary. No code fence.`;

const SUMMARY_SYSTEM = "You are a real estate disclosure specialist licensed across all US states. Synthesize the seller's interview answers AND any pre-listing inspection findings into a clean seller-facing summary mirroring what will appear on the property's actual state's formal disclosure forms. Flag any item that materially affects price or buyer interest.";

const SUMMARY_PROMPT = (address, answers, inspectionFindings, note) => `Property: ${address}

${inspectionFindings && Object.keys(inspectionFindings).length ? `Inspection findings (must be disclosed — already known):
${JSON.stringify(inspectionFindings)}

` : ""}Interview answers:
${answers.map(a => `- [${a.section}] ${a.q}\n  Answer: ${a.answer}${a.note ? `\n  Note: ${a.note}` : ""}`).join("\n")}
${note ? `\nSeller's guidance for this regeneration — factor this in: ${note}\n` : ""}
Output in markdown:

**Summary**
(2-3 sentences for the seller — what we'll be disclosing.)

**By section**
(one bullet per section that appeared in the interview answers above, using that
section's actual name for this property's state — 1-2 sentences each. Always
include a lead-paint line if the home is pre-1978, and an HOA line, N/A if none.)

**Items that may affect price or buyer interest**
- (bullets — only material flags; "None" if nothing)`;

const safeParseJSON = (text) => {
  if (!text) return null;
  const cleaned = text.replace(/```(?:json)?/gi, "").trim();
  const start = cleaned.indexOf("[");
  const end = cleaned.lastIndexOf("]");
  if (start < 0 || end < 0) return null;
  try { return JSON.parse(cleaned.slice(start, end + 1)); } catch { return null; }
};

export default function DisclosuresStage({ state, journey, update }) {
  const auth = useAuth();
  const prepData = getStageState(journey, "prep").data || {};
  const inspectionFindings = prepData.inspection_findings || null;
  const inspectionBooked = Boolean(prepData.accepted_at);
  // Until inspection findings actually arrive, treat this stage as awaiting if
  // an inspector has been booked. Sellers can still answer the non-inspectable
  // questions in parallel.
  const awaitingInspection = inspectionBooked && !inspectionFindings;

  const [questions, setQuestions] = useState(state.data?.questions || []);
  const [answers, setAnswers] = useState(state.data?.answers || {});
  const [summary, setSummary] = useState(state.data?.summary || "");
  const [phase, setPhase] = useState(state.data?.summary ? "summary" : (state.data?.questions ? "interview" : "loading"));
  const [err, setErr] = useState(null);
  const fired = useRef(false);

  const downloadPDF = () => openDisclosurePDF(journey.address, questions, answers, summary);

  const loadQuestions = async () => {
    setPhase("loading"); setErr(null);
    update({ status: "in_progress" });
    track("seller_disclosures_questions_load", { has_inspection: Boolean(inspectionFindings) });
    try {
      const year = journey.property?.property?.[0]?.building?.yearbuilt;
      // Only cache the question set when there's no inspection-driven prefill,
      // because findings change the prompt and the cache key wouldn't capture that.
      const cacheable = !inspectionFindings;
      const { text } = await callAI({
        system: Q_SYSTEM,
        prompt: Q_PROMPT(journey.address, year, inspectionFindings),
        maxTokens: 1500,
        cacheKey: cacheable ? `disclosures_q:${normalizeAddressKey(journey.address)}` : undefined,
        cacheType: cacheable ? "disclosures_q" : undefined,
      });
      const parsed = safeParseJSON(text);
      if (!parsed || !Array.isArray(parsed) || !parsed.length) throw new Error("bad-json");
      setQuestions(parsed);
      setPhase("interview");
      update({ data: { questions: parsed, answers: {}, summary: "" } });
    } catch {
      setErr("Couldn't load the question set. Try again.");
      setPhase("error");
    }
  };

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!state.data?.questions) loadQuestions();
  }, []);

  const setAns = (qid, patch) => {
    const next = { ...answers, [qid]: { ...(answers[qid] || {}), ...patch } };
    setAnswers(next);
    update({ data: { questions, answers: next, summary } });
  };

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id]?.answer);

  const synthesize = async (regenNote = "") => {
    setPhase("synthesizing");
    track("seller_disclosures_synthesize", { has_note: Boolean(regenNote) });
    try {
      const enriched = questions.map((q) => ({
        section: q.section,
        q: q.q,
        answer: answers[q.id]?.answer || "no",
        note: answers[q.id]?.note || "",
      }));
      const { text } = await callAI({
        system: SUMMARY_SYSTEM,
        prompt: SUMMARY_PROMPT(journey.address, enriched, inspectionFindings, regenNote),
        maxTokens: 1000,
      });
      const generatedAt = new Date().toISOString();
      setSummary(text);
      setPhase("summary");
      update({
        status: "done",
        data: { questions, answers, summary: text, generated_at: generatedAt },
      });
      track("seller_disclosures_done");
      // Auto-save disclosure as a persistent message so broker + TC can access it anytime.
      const brokerId = journey?.broker_id;
      const clientId = auth?.user?.id;
      if (supabase && brokerId && clientId) {
        const payload = JSON.stringify({ address: journey.address, questions, answers, summary: text, generated_at: generatedAt });
        // Replace any existing disclosure message for this pair to avoid duplicates.
        await supabase.from("messages").delete()
          .eq("broker_id", brokerId).eq("client_id", clientId).eq("message_type", "disclosure");
        await supabase.from("messages").insert({
          broker_id: brokerId, client_id: clientId,
          from_role: "seller", body: payload, message_type: "disclosure",
        });
      }
    } catch {
      setErr("Synthesis failed. Try again.");
      setPhase("interview");
    }
  };

  return (
    <>
      <div className="s-soon" style={{ marginBottom: 18 }}>
        <div className="s-soon-row">
          <div className="s-soon-bullet">★</div>
          <div className="s-soon-text">
            <strong>AI-led interview.</strong> Instead of filling 30 pages, answer a handful of plain-English questions.
            We synthesize them into your TDS, SPQ, NHD, lead-paint, and HOA disclosures.
            <span className="s-ai-pill">★ AI · Disclosure synthesis</span>
          </div>
        </div>
        {inspectionFindings && (
          <div className="s-soon-row">
            <div className="s-soon-bullet">✓</div>
            <div className="s-soon-text">
              <strong>Inspection findings pre-filled.</strong> Defects flagged by your pre-listing inspector are already
              captured below — we won't re-ask. You only confirm the items an inspector can't see.
            </div>
          </div>
        )}
        {awaitingInspection && (
          <div className="s-soon-row">
            <div className="s-soon-bullet">⏳</div>
            <div className="s-soon-text">
              <strong>Waiting on inspection report.</strong> Your inspector's findings will pre-fill some of this
              automatically. You can answer the interview questions below in the meantime.
            </div>
          </div>
        )}
      </div>

      {phase === "loading" && <Spinner label="Generating your disclosure questions…" />}

      {phase === "error" && (
        <div>
          <div className="s-intake-err" style={{ marginBottom: 12 }}>{err}</div>
          <button type="button" className="s-btn amber" onClick={loadQuestions}>Try again</button>
        </div>
      )}

      {(phase === "interview" || phase === "synthesizing") && (
        <>
          {questions.map((q) => {
            const a = answers[q.id] || {};
            return (
              <div key={q.id} className="s-q">
                <div className="s-q-text">
                  <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: "#9aa9a0", letterSpacing: "0.08em", marginRight: 8 }}>{q.section}</span>
                  {q.q}
                </div>
                <div className="s-q-row">
                  <button
                    type="button"
                    className={`s-q-pill${a.answer === "yes" ? " picked yes" : ""}`}
                    onClick={() => setAns(q.id, { answer: "yes" })}
                  >Yes</button>
                  <button
                    type="button"
                    className={`s-q-pill${a.answer === "no" ? " picked no" : ""}`}
                    onClick={() => setAns(q.id, { answer: "no" })}
                  >No</button>
                  {a.answer === "yes" && (
                    <input
                      className="s-q-note"
                      placeholder={q.note_required_if_yes ? "Details (required)" : "Optional details"}
                      value={a.note || ""}
                      onChange={(e) => setAns(q.id, { note: e.target.value })}
                    />
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ marginTop: 16 }}>
            {phase === "synthesizing"
              ? <Spinner label="Synthesizing your disclosure summary…" />
              : (
                <button
                  type="button"
                  className="s-btn amber"
                  onClick={() => synthesize()}
                  disabled={!allAnswered}
                >Generate disclosure summary →</button>
              )}
          </div>
        </>
      )}

      {phase === "summary" && (
        <>
          <div className="s-prose" dangerouslySetInnerHTML={{ __html: renderMD(summary) }} />

          <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <button type="button" className="s-btn amber" onClick={downloadPDF}>⬇ Download PDF</button>
            {supabase && journey?.broker_id && (
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#7ecfb0", letterSpacing: "0.04em" }}>
                ✓ Saved to your broker's Messages &amp; Timeline
              </span>
            )}
          </div>
          <div style={{ marginTop: 10 }}>
            <button type="button" className="s-link-btn" onClick={() => setPhase("interview")}>edit answers</button>
          </div>
          <RegenerateBar
            onRegenerate={(note) => synthesize(note)}
            loading={false}
            placeholder="e.g. soften the tone, flag the roof more prominently…"
          />
        </>
      )}
    </>
  );
}
